/* ============================================================
   Inessa — Build Your Own Bundle engine (new file, 14 AUG 2026)
   Vanilla JS, no dependencies. Powers sections/bb-bundle-builder.liquid
   - add / remove / quantity per product
   - one-time vs subscribe & save tiers
   - progress bar + dynamic unlock messaging (all quantity cases)
   - sticky mobile bar + bottom-sheet drawer
   - V2 (07 SEP 2026): purchase options are radio cards (Subscribe & Save w/ benefits, One-time)
   - AJAX checkout (POST /cart/add.js -> /checkout)
   - order tagging (23 SEP 2026): the cart gets the attribute
     "Order source: Build Your Own Bundle"; a Shopify Flow workflow
     (Order created -> attribute present -> add tag) tags the order
   NOTE: discounts themselves are configured separately in the
   discount app later; this UI previews the tier savings only.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'bb:bundle:v1';
  /* cart attribute that marks orders placed from this page — Shopify Flow
     reads it and adds the "build your own bundle" order tag. Keep the key
     and value in sync with the Flow condition. */
  var SOURCE_ATTRIBUTE = { key: 'Order source', value: 'Build Your Own Bundle' };
  var instances = {};

  /* ---------- helpers ---------- */

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withDecimals = parts.join('.');
    var noDecimals = parts[0];
    return (format || '£{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode — state just won't persist */
    }
  }

  /* ---------- instance ---------- */

  function BundleBuilder(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.moneyFormat = root.getAttribute('data-money-format') || '£{{amount}}';
    this.tiers = {
      onetime: [
        { qty: parseInt(root.getAttribute('data-ot-t1-qty'), 10) || 3, pct: parseFloat(root.getAttribute('data-ot-t1-pct')) || 15 },
        { qty: parseInt(root.getAttribute('data-ot-t2-qty'), 10) || 4, pct: parseFloat(root.getAttribute('data-ot-t2-pct')) || 20 }
      ],
      subscribe: [
        { qty: parseInt(root.getAttribute('data-ot-t1-qty'), 10) || 3, pct: parseFloat(root.getAttribute('data-sub-t1-pct')) || 20 },
        { qty: parseInt(root.getAttribute('data-ot-t2-qty'), 10) || 4, pct: parseFloat(root.getAttribute('data-sub-t2-pct')) || 25 }
      ]
    };

    /* optional discount codes per tier, applied on checkout via /discount/CODE */
    this.codes = {
      onetime: [root.getAttribute('data-ot-t1-code') || '', root.getAttribute('data-ot-t2-code') || ''],
      subscribe: [root.getAttribute('data-sub-t1-code') || '', root.getAttribute('data-sub-t2-code') || '']
    };

    /* REAL percentage of each subscription code. It stacks on the selling
       plan's built-in discount, so the effective saving is NOT the marketing
       tier pct — totals must be computed the way checkout computes them. */
    this.subCodePcts = [
      parseFloat(root.getAttribute('data-sub-t1-code-pct')) || 0,
      parseFloat(root.getAttribute('data-sub-t2-code-pct')) || 0
    ];

    this.els = {
      grid: root.querySelector('[data-bb-grid]'),
      panel: root.querySelector('[data-bb-panel]'),
      panelHome: root.querySelector('[data-bb-panel-home]'),
      itemsWrap: root.querySelector('[data-bb-items]'),
      emptyTpl: root.querySelector('[data-bb-empty-template]'),
      itemTpl: root.querySelector('[data-bb-item-template]'),
      message: root.querySelector('[data-bb-message]'),
      fill: root.querySelector('[data-bb-fill]'),
      knob: root.querySelector('[data-bb-knob]'),
      stops: root.querySelectorAll('[data-bb-stop]'),
      subtotal: root.querySelector('[data-bb-subtotal]'),
      savingRow: root.querySelector('[data-bb-saving-row]'),
      savingLabel: root.querySelector('[data-bb-saving-label]'),
      saving: root.querySelector('[data-bb-saving]'),
      total: root.querySelector('[data-bb-total]'),
      checkout: root.querySelector('[data-bb-checkout]'),
      bar: root.querySelector('[data-bb-bar]'),
      barCount: root.querySelector('[data-bb-bar-count]'),
      barTotal: root.querySelector('[data-bb-bar-total]'),
      barCompare: root.querySelector('[data-bb-bar-compare]'),
      drawer: root.querySelector('[data-bb-drawer]'),
      drawerBody: root.querySelector('[data-bb-drawer-body]'),
      sentinel: root.querySelector('[data-bb-sentinel]')
    };

    /* product meta from cards */
    this.products = {};
    var self = this;
    root.querySelectorAll('[data-bb-card]').forEach(function (card) {
      var id = card.getAttribute('data-product-id');
      self.products[id] = {
        id: id,
        variantId: card.getAttribute('data-variant-id'),
        sellingPlanId: card.getAttribute('data-selling-plan') || '',
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        planPrice: parseInt(card.getAttribute('data-plan-price'), 10) || parseInt(card.getAttribute('data-price'), 10) || 0,
        title: card.getAttribute('data-title') || '',
        image: card.getAttribute('data-image') || '',
        url: card.getAttribute('data-url') || '',
        card: card,
        reviewsHtml: (function () {
          var r = card.querySelector('[data-bb-card-reviews]');
          return r ? r.innerHTML : '';
        })()
      };
    });

    /* state */
    var saved = readState();
    /* first visit: the section's pre-selected option; returning visitors keep their choice */
    var defaultMode = root.getAttribute('data-default-mode') === 'onetime' ? 'onetime' : 'subscribe';
    this.mode = saved && (saved.mode === 'subscribe' || saved.mode === 'onetime') ? saved.mode : defaultMode;
    this.items = {}; // productId -> qty
    if (saved && saved.items) {
      for (var pid in saved.items) {
        if (this.products[pid] && saved.items[pid] > 0) {
          this.items[pid] = Math.min(99, parseInt(saved.items[pid], 10) || 0);
        }
      }
    }
    if (!root.hasAttribute('data-show-subscribe')) this.mode = 'onetime';

    this.drawerOpen = false;
    this.barUnlocked = false;

    this.bindEvents();
    this.handleBreakpoint();
    this.render();
  }

  BundleBuilder.prototype.persist = function () {
    writeState({ mode: this.mode, items: this.items });
  };

  BundleBuilder.prototype.itemCount = function () {
    var n = 0;
    for (var pid in this.items) n += this.items[pid];
    return n;
  };

  BundleBuilder.prototype.subtotalCents = function () {
    var t = 0;
    for (var pid in this.items) t += this.products[pid].price * this.items[pid];
    return t;
  };

  BundleBuilder.prototype.activeTiers = function () {
    return this.tiers[this.mode];
  };

  BundleBuilder.prototype.currentPct = function () {
    var n = this.itemCount();
    var tiers = this.activeTiers();
    if (n >= tiers[1].qty) return tiers[1].pct;
    if (n >= tiers[0].qty) return tiers[0].pct;
    return 0;
  };

  /* index of the tier currently reached: -1 / 0 / 1 */
  BundleBuilder.prototype.currentTierIndex = function () {
    var n = this.itemCount();
    var tiers = this.activeTiers();
    if (n >= tiers[1].qty) return 1;
    if (n >= tiers[0].qty) return 0;
    return -1;
  };

  /* Mirror of what checkout charges, to the penny.
     - one-time: order-level % code -> Shopify floors the discount amount.
     - subscribe: each line is charged the selling-plan price (built-in
       Subscribe & Save discount), then the code % is applied PER LINE and
       floored — so it compounds instead of adding up. */
  BundleBuilder.prototype.computeTotals = function () {
    var subtotal = this.subtotalCents(); // one-time value of the bundle
    var tierIdx = this.currentTierIndex();
    var total;

    if (this.mode === 'subscribe') {
      var codePct = tierIdx >= 0 ? this.subCodePcts[tierIdx] : 0;
      total = 0;
      for (var pid in this.items) {
        var planPrice = this.products[pid].planPrice;
        var qty = this.items[pid];
        /* checkout floors the discount PER UNIT, then multiplies by qty
           (verified: 2 x £26.99 @15% -> -£8.08, i.e. floor(4.0485) x 2) */
        var unitDiscount = Math.floor((planPrice * codePct) / 100 + 1e-6);
        total += (planPrice - unitDiscount) * qty;
      }
    } else {
      var pct = tierIdx >= 0 ? this.activeTiers()[tierIdx].pct : 0;
      total = subtotal - Math.floor((subtotal * pct) / 100 + 1e-6);
    }

    return { subtotal: subtotal, total: total, saving: subtotal - total };
  };

  /* ---------- events ---------- */

  BundleBuilder.prototype.bindEvents = function () {
    var self = this;

    this.onClick = function (evt) {
      var t = evt.target;
      var btn;

      if ((btn = t.closest('[data-bb-add]'))) {
        var card = btn.closest('[data-bb-card]');
        if (card) self.setQty(card.getAttribute('data-product-id'), 1);
      } else if ((btn = t.closest('[data-bb-plus]'))) {
        var cardP = btn.closest('[data-bb-card]');
        if (cardP) {
          var idP = cardP.getAttribute('data-product-id');
          self.setQty(idP, (self.items[idP] || 0) + 1);
        }
      } else if ((btn = t.closest('[data-bb-minus]'))) {
        var cardM = btn.closest('[data-bb-card]');
        if (cardM) {
          var idM = cardM.getAttribute('data-product-id');
          self.setQty(idM, (self.items[idM] || 0) - 1);
        }
      } else if ((btn = t.closest('[data-bb-remove]'))) {
        var idR = btn.getAttribute('data-bb-remove');
        self.setQty(idR, (self.items[idR] || 0) - 1);
      } else if ((btn = t.closest('[data-bb-mode]'))) {
        self.mode = btn.getAttribute('data-bb-mode') === 'subscribe' ? 'subscribe' : 'onetime';
        self.persist();
        self.render();
      } else if ((btn = t.closest('[data-bb-checkout]'))) {
        self.checkout(btn);
      } else if (t.closest('[data-bb-bar-toggle]') || t.closest('[data-bb-bar-open]')) {
        self.openDrawer();
      } else if (t.closest('[data-bb-drawer-close]') || t.closest('[data-bb-drawer] .bb-drawer__scrim')) {
        self.closeDrawer();
      }
    };
    this.root.addEventListener('click', this.onClick);

    this.onKeydown = function (evt) {
      if (evt.key === 'Escape' && self.drawerOpen) self.closeDrawer();
    };
    document.addEventListener('keydown', this.onKeydown);

    /* sticky bar visibility: show once the user scrolls past the
       heading/pills (sentinel sits right above the product grid) */
    this.onScroll = function () {
      if (self.scrollTick) return;
      self.scrollTick = true;
      requestAnimationFrame(function () {
        self.scrollTick = false;
        self.updateBarVisibility();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.mql = window.matchMedia('(min-width: 990px)');
    this.onBreakpoint = function () {
      self.handleBreakpoint();
    };
    if (this.mql.addEventListener) this.mql.addEventListener('change', this.onBreakpoint);
    else this.mql.addListener(this.onBreakpoint);
  };

  BundleBuilder.prototype.destroy = function () {
    this.root.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.mql) {
      if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onBreakpoint);
      else this.mql.removeListener(this.onBreakpoint);
    }
    this.closeDrawer();
  };

  /* ---------- state changes ---------- */

  BundleBuilder.prototype.setQty = function (pid, qty) {
    if (!this.products[pid]) return;
    qty = Math.max(0, Math.min(99, qty));
    if (qty === 0) delete this.items[pid];
    else this.items[pid] = qty;
    this.persist();
    this.render();
  };

  /* ---------- drawer / bar ---------- */

  BundleBuilder.prototype.handleBreakpoint = function () {
    if (this.mql.matches) {
      /* desktop: panel lives in the sticky aside */
      this.closeDrawer();
      if (this.els.panel.parentNode !== this.els.panelHome) {
        this.els.panelHome.appendChild(this.els.panel);
      }
    }
    this.updateBarVisibility();
  };

  BundleBuilder.prototype.openDrawer = function () {
    if (this.mql.matches || this.drawerOpen) return;
    this.drawerOpen = true;
    this.els.drawerBody.appendChild(this.els.panel);
    this.els.drawer.classList.add('is-open');
    document.body.classList.add('bb-lock-scroll');
    this.els.bar.classList.remove('is-visible');
    var close = this.els.drawer.querySelector('[data-bb-drawer-close]');
    if (close) close.focus({ preventScroll: true });
  };

  BundleBuilder.prototype.closeDrawer = function () {
    if (!this.drawerOpen) {
      if (this.els.drawer) this.els.drawer.classList.remove('is-open');
      document.body.classList.remove('bb-lock-scroll');
      return;
    }
    this.drawerOpen = false;
    this.els.drawer.classList.remove('is-open');
    document.body.classList.remove('bb-lock-scroll');
    if (!this.mql.matches) {
      this.els.panelHome.appendChild(this.els.panel);
    }
    this.updateBarVisibility();
    var toggle = this.els.bar.querySelector('[data-bb-bar-toggle]');
    if (toggle) toggle.focus({ preventScroll: true });
  };

  BundleBuilder.prototype.updateBarVisibility = function () {
    if (!this.els.bar) return;
    if (this.mql.matches || this.drawerOpen) {
      this.els.bar.classList.remove('is-visible');
      return;
    }
    var rect = this.els.sentinel.getBoundingClientRect();
    var show = rect.top < 0; /* scrolled past heading + pills */
    this.els.bar.classList.toggle('is-visible', show);
  };

  /* ---------- rendering ---------- */

  BundleBuilder.prototype.render = function () {
    this.renderCards();
    this.renderToggle();
    this.renderProgress();
    this.renderItems();
    this.renderTotals();
    this.renderBar();
    this.updateBarVisibility();
  };

  BundleBuilder.prototype.renderCards = function () {
    for (var pid in this.products) {
      var p = this.products[pid];
      var qty = this.items[pid] || 0;
      p.card.classList.toggle('is-added', qty > 0);
      var qtyEl = p.card.querySelector('[data-bb-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
    }
  };

  BundleBuilder.prototype.renderToggle = function () {
    var self = this;
    this.root.querySelectorAll('[data-bb-mode]').forEach(function (btn) {
      var on = btn.getAttribute('data-bb-mode') === self.mode;
      /* V2 option cards are radios; is-selected drives the cream/gold styling
         (the badge above the subscribe card also carries data-bb-mode so a
         tap on it selects the option, but it is not a radio itself) */
      if (btn.tagName === 'BUTTON') btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.classList.toggle('is-selected', on);
    });
  };

  BundleBuilder.prototype.renderProgress = function () {
    var n = this.itemCount();
    var tiers = this.activeTiers();
    var maxQty = tiers[1].qty;
    var frac = Math.min(1, n / maxQty);
    var pctPos = (frac * 100).toFixed(1) + '%';
    if (this.els.fill) this.els.fill.style.width = pctPos;
    if (this.els.knob) this.els.knob.style.left = pctPos;

    this.els.stops.forEach(function (stop, i) {
      var tier = tiers[i];
      if (!tier) return;
      stop.style.left = ((tier.qty / maxQty) * 100).toFixed(1) + '%';
      stop.classList.toggle('is-reached', n >= tier.qty);
    });

    /* dynamic unlock messaging — covers every quantity case */
    var msg = '';
    var t1 = tiers[0];
    var t2 = tiers[1];
    if (n >= t2.qty) {
      msg = 'Maximum saving unlocked —<strong>' + t2.pct + '% off</strong>';
    } else if (n >= t1.qty) {
      var moreToMax = t2.qty - n;
      msg =
        '<strong>' + t1.pct + '% off</strong> unlocked — add ' +
        (moreToMax === 1 ? '1 more product' : moreToMax + ' more products') +
        ' to get <strong>' + t2.pct + '% off</strong>';
    } else if (n === 0) {
      msg = 'Add any ' + t1.qty + ' products to unlock <strong>' + t1.pct + '% off</strong>';
    } else {
      var more = t1.qty - n;
      msg =
        'Add ' + (more === 1 ? 'any 1 more product' : 'any ' + more + ' more products') +
        ' to unlock <strong>' + t1.pct + '% off</strong>';
    }
    if (this.els.message) this.els.message.innerHTML = msg;
  };

  BundleBuilder.prototype.renderItems = function () {
    var wrap = this.els.itemsWrap;
    if (!wrap) return;
    wrap.innerHTML = '';

    var count = this.itemCount();
    if (count === 0) {
      var empty = this.els.emptyTpl.content.cloneNode(true);
      wrap.appendChild(empty);
      return;
    }

    /* one row per unit, matching the design (qty 3 = three rows) */
    for (var pid in this.items) {
      var p = this.products[pid];
      for (var u = 0; u < this.items[pid]; u++) {
        var frag = this.els.itemTpl.content.cloneNode(true);
        var row = frag.querySelector('.bb-item');
        row.querySelector('[data-bb-item-title]').textContent = p.title;
        row.querySelector('[data-bb-item-price]').textContent = formatMoney(p.price, this.moneyFormat);
        var img = row.querySelector('[data-bb-item-img]');
        if (p.image) {
          img.src = p.image;
          img.alt = p.title;
        } else {
          img.remove();
        }
        var rev = row.querySelector('[data-bb-item-reviews]');
        if (p.reviewsHtml) rev.innerHTML = p.reviewsHtml;
        else rev.remove();
        row.querySelector('[data-bb-remove]').setAttribute('data-bb-remove', pid);
        wrap.appendChild(frag);
      }
    }
  };

  BundleBuilder.prototype.renderTotals = function () {
    var t = this.computeTotals();
    var pct = this.currentPct();

    if (this.els.subtotal) this.els.subtotal.textContent = formatMoney(t.subtotal, this.moneyFormat);
    if (this.els.savingRow) this.els.savingRow.style.display = t.saving > 0 ? '' : 'none';
    if (this.els.savingLabel) {
      /* subscribe mode: the exact amount comes from plan price + code, so a
         flat "(X%)" would not match — show the amount without a percentage */
      this.els.savingLabel.textContent = this.mode === 'subscribe' ? 'Bundle saving' : 'Bundle saving (' + pct + '%)';
    }
    if (this.els.saving) this.els.saving.textContent = '−' + formatMoney(t.saving, this.moneyFormat);
    if (this.els.total) this.els.total.textContent = formatMoney(t.total, this.moneyFormat);

    if (this.els.checkout) {
      this.els.checkout.classList.toggle('is-disabled', this.itemCount() === 0);
      this.els.checkout.setAttribute('aria-disabled', this.itemCount() === 0 ? 'true' : 'false');
    }
  };

  BundleBuilder.prototype.renderBar = function () {
    if (!this.els.bar) return;
    var n = this.itemCount();
    var t = this.computeTotals();

    this.els.barCount.textContent = n === 1 ? '1 Item' : n + ' Items';
    this.els.barTotal.textContent = formatMoney(t.total, this.moneyFormat);
    if (this.els.barCompare) {
      if (t.saving > 0) {
        this.els.barCompare.style.display = '';
        this.els.barCompare.textContent = formatMoney(t.subtotal, this.moneyFormat);
      } else {
        this.els.barCompare.style.display = 'none';
      }
    }
  };

  /* ---------- checkout ---------- */

  BundleBuilder.prototype.checkout = function (btn) {
    var self = this;
    if (this.itemCount() === 0 || btn.classList.contains('is-loading')) return;

    var items = [];
    for (var pid in this.items) {
      var p = this.products[pid];
      var line = { id: parseInt(p.variantId, 10), quantity: this.items[pid] };
      if (this.mode === 'subscribe' && p.sellingPlanId) {
        line.selling_plan = parseInt(p.sellingPlanId, 10);
      }
      items.push(line);
    }

    var label = btn.querySelector('[data-bb-checkout-label]');
    var original = label ? label.textContent : '';
    btn.classList.add('is-loading');
    if (label) label.textContent = btn.getAttribute('data-loading-text') || 'Adding…';

    /* empty any pre-existing cart first so checkout contains ONLY the bundle */
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' }
    })
      .catch(function () { /* best effort — still proceed to add the bundle */ })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (res) {
        if (!res.ok) return res;
        /* mark the cart as a BYOB order (best effort — never blocks checkout) */
        var attributes = {};
        attributes[SOURCE_ATTRIBUTE.key] = SOURCE_ATTRIBUTE.value;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        })
          .catch(function () { /* noop */ })
          .then(function () { return res; });
      })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error((data && (data.description || data.message)) || 'Could not add the bundle to the cart.');
          });
        }
        return res.json();
      })
      .then(function () {
        /* fresh session on the next visit */
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) { /* noop */ }
        /* apply the tier's discount code (if configured) on the way to checkout */
        var tiers = self.tiers[self.mode === 'subscribe' ? 'subscribe' : 'onetime'];
        var codes = self.codes[self.mode === 'subscribe' ? 'subscribe' : 'onetime'];
        var count = self.itemCount();
        var code = '';
        if (count >= tiers[1].qty) code = codes[1] || codes[0];
        else if (count >= tiers[0].qty) code = codes[0];
        window.location.href = code
          ? '/discount/' + encodeURIComponent(code) + '?redirect=%2Fcheckout'
          : '/checkout';
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        if (label) label.textContent = original;
        window.alert(err && err.message ? err.message : 'Something went wrong. Please try again.');
      });
  };

  /* ---------- boot ---------- */

  function init(root) {
    if (!root) return;
    var id = root.getAttribute('data-section-id');
    if (instances[id]) instances[id].destroy();
    instances[id] = new BundleBuilder(root);
  }

  function initAll() {
    document.querySelectorAll('[data-bb-builder]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-bb-builder]');
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var root = evt.target.querySelector('[data-bb-builder]');
    if (root) {
      var id = root.getAttribute('data-section-id');
      if (instances[id]) {
        instances[id].destroy();
        delete instances[id];
      }
    }
  });
})();
